const REMANUFACTURING_SERVICE_CLASS = "remanufacturing service";

/** Canonical values persisted on orders and in additionalAttributes.listingClass. */
export const MARKETPLACE_LISTING_CLASS = {
  PRODUCT: "Product",
  REMANUFACTURING_SERVICE: "Remanufacturing service",
};

const LISTING_CLASS_FIELD_KEYS = [
  "listingClass",
  "listing_class",
  "productClass",
  "product_class",
];

const LISTING_CLASS_ATTRIBUTE_KEYS = [
  "listingClass",
  "listing_class",
  "class",
  "productClass",
  "product_class",
];

/**
 * @param {unknown} value
 * @returns {string}
 */
function stringifyListingClassValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object") {
    const record = /** @type {Record<string, unknown>} */ (value);
    const resolved = record.value ?? record.label ?? "";
    return String(resolved).trim();
  }
  return String(value).trim();
}

/**
 * @param {unknown} value
 * @returns {string}
 */
export function normalizeProductListingClass(value) {
  return stringifyListingClassValue(value).toLowerCase();
}

/**
 * @param {unknown} value
 * @returns {typeof MARKETPLACE_LISTING_CLASS.PRODUCT | typeof MARKETPLACE_LISTING_CLASS.REMANUFACTURING_SERVICE}
 */
export function toMarketplaceListingClassCanonical(value) {
  if (isRemanufacturingServiceClass(value)) {
    return MARKETPLACE_LISTING_CLASS.REMANUFACTURING_SERVICE;
  }
  return MARKETPLACE_LISTING_CLASS.PRODUCT;
}

/**
 * Resolves marketplace listing type from product payloads (not schema parameter class).
 * @param {unknown} source
 * @returns {string}
 */
export function resolveProductListingClass(source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return "";
  }

  const record = /** @type {Record<string, unknown>} */ (source);

  for (const key of LISTING_CLASS_FIELD_KEYS) {
    const resolved = stringifyListingClassValue(record[key]);
    if (resolved) return resolved;
  }

  const additionalAttributes = record.additionalAttributes;
  if (
    additionalAttributes &&
    typeof additionalAttributes === "object" &&
    !Array.isArray(additionalAttributes)
  ) {
    const attrs = /** @type {Record<string, unknown>} */ (additionalAttributes);
    for (const key of LISTING_CLASS_ATTRIBUTE_KEYS) {
      const resolved = stringifyListingClassValue(attrs[key]);
      if (resolved) return resolved;
    }
  }

  const schemaClass = stringifyListingClassValue(record.class);
  if (schemaClass && isRemanufacturingServiceClass(schemaClass)) {
    return MARKETPLACE_LISTING_CLASS.REMANUFACTURING_SERVICE;
  }

  return "";
}

/**
 * Derives the canonical listing class stored on orders and additionalAttributes.
 * @param {Record<string, unknown>|null|undefined} product
 * @param {unknown} [schemaProductClass] - Product schema class (`product.class`), not listing type.
 * @returns {typeof MARKETPLACE_LISTING_CLASS.PRODUCT | typeof MARKETPLACE_LISTING_CLASS.REMANUFACTURING_SERVICE}
 */
export function deriveMarketplaceListingClass(product, schemaProductClass = "") {
  const resolved = resolveProductListingClass(product);
  if (resolved) {
    return toMarketplaceListingClassCanonical(resolved);
  }
  if (isRemanufacturingServiceClass(schemaProductClass)) {
    return MARKETPLACE_LISTING_CLASS.REMANUFACTURING_SERVICE;
  }
  return MARKETPLACE_LISTING_CLASS.PRODUCT;
}

/**
 * Translation key for a marketplace order listing class label.
 * @param {unknown} listingClass
 * @returns {"listingTypeProduct"|"remanufacturingServiceBadge"}
 */
export function listingClassLabelKey(listingClass) {
  return isRemanufacturingServiceClass(listingClass)
    ? "remanufacturingServiceBadge"
    : "listingTypeProduct";
}

/**
 * @param {Record<string, unknown>|null|undefined} product
 * @returns {Record<string, unknown>|null|undefined}
 */
export function normalizeProductForCatalog(product) {
  if (!product || typeof product !== "object") return product;

  const resolvedClass = resolveProductListingClass(product);
  if (!resolvedClass) return product;

  const listingClass = toMarketplaceListingClassCanonical(resolvedClass);
  const additionalAttributes =
    product.additionalAttributes &&
    typeof product.additionalAttributes === "object" &&
    !Array.isArray(product.additionalAttributes)
      ? {
          ...product.additionalAttributes,
          listingClass,
        }
      : { listingClass };

  if (
    product.listingClass === listingClass &&
    product.additionalAttributes?.listingClass === listingClass
  ) {
    return product;
  }

  return {
    ...product,
    listingClass,
    additionalAttributes,
  };
}

/**
 * @param {unknown} productClass
 * @returns {boolean}
 */
export function isRemanufacturingServiceClass(productClass) {
  return (
    normalizeProductListingClass(productClass) === REMANUFACTURING_SERVICE_CLASS
  );
}

/**
 * @param {{ class?: unknown } | null | undefined} product
 * @returns {boolean}
 */
export function isRemanufacturingServiceProduct(product) {
  return isRemanufacturingServiceClass(resolveProductListingClass(product));
}

/**
 * @param {{ class?: unknown } | null | undefined} product
 * @returns {"product"|"remanufacturing-service"}
 */
export function getProductListingType(product) {
  return isRemanufacturingServiceProduct(product)
    ? "remanufacturing-service"
    : "product";
}
